/**
 * The portal run-out, the retention and the Löschvorschlag of an inaktiv customer (P6-S7, Stefan's P6
 * steps 23-25, 10.4, Kap. 8 Legal Hold, #13): the words, the error codes, the events and the read model.
 * The German words live here only (rule #4); api and web render the same sentences.
 */
import { addDaysIso, formatIsoDayDe } from './mandate-termination';
import { zurichDateOf } from './zurich-day';

/** The Löschvorschlag task (process customer_retention, meta.kind). */
export const RETENTION_TASK = {
  LOESCHVORSCHLAG: { kind: 'loeschvorschlag', titlePrefix: 'Löschvorschlag: ' },
} as const;

/** The closed reasons the retention writes into task.meta.closed_reason. */
export const RETENTION_CLOSED_REASON = {
  LEGAL_HOLD: 'legal_hold',
  MERGE: 'merge',
  DUPLICATE: 'duplicate',
  REPROPOSE: 'repropose',
  APPROVED: 'loeschung_freigegeben',
  DECLINED: 'loeschung_abgelehnt',
} as const;

/** One body per refusal (code + German sentence). */
export const RETENTION_ERROR = {
  LEGAL_HOLD_STATUS_ILLEGAL: {
    code: 'legal_hold_status_illegal',
    message: 'Ein Legal Hold kann nur für einen gekündigten oder inaktiven Kunden gesetzt werden.',
  },
  LEGAL_HOLD_ACTIVE: { code: 'legal_hold_active', message: 'Für diesen Kunden besteht ein Legal Hold.' },
  DELETION_PROPOSAL_MISSING: { code: 'deletion_proposal_missing', message: 'Für diesen Kunden liegt kein offener Löschvorschlag vor.' },
  CONFIRMATION_MISMATCH: { code: 'confirmation_mismatch', message: 'Die Bestätigung stimmt nicht mit dem Kunden überein.' },
  DELETION_APPROVED: { code: 'deletion_approved', message: 'Die Löschung dieses Kunden ist bereits freigegeben.' },
  REPROPOSE_ILLEGAL: { code: 'repropose_illegal', message: 'Ein Löschvorschlag ist nur für einen inaktiven Kunden mit abgelaufener Aufbewahrung möglich.' },
  RETENTION_NOT_DUE: { code: 'retention_not_due', message: 'Die Aufbewahrungsfrist dieses Kunden ist noch nicht abgelaufen.' },
  USE_RETENTION_ROUTES: { code: 'use_retention_routes', message: 'Über Löschung freigeben oder ablehnen abschliessen.' },
  APPROVER_UNKNOWN: { code: 'approver_unknown', message: 'Ihr Benutzer ist in Brokly nicht erfasst. Die Löschung kann nicht freigegeben werden.' },
} as const;

/**
 * The German sentence of a refused retention route: the RETENTION_ERROR message of the error's `code`,
 * else `fallback` (network, 403, an unknown code). Duck-typed on `code` so the web's ApiError and a plain
 * object both work.
 */
export function retentionErrorText(err: unknown, fallback: string): string {
  const code = typeof err === 'object' && err !== null && 'code' in err ? (err as { code?: unknown }).code : undefined;
  if (typeof code !== 'string') return fallback;
  return Object.values(RETENTION_ERROR).find((e) => e.code === code)?.message ?? fallback;
}

/** A deaktiviert portal login (after the password check): 403 with this body; the web shows the sentence. */
export const PORTAL_ACCESS_DEACTIVATED = {
  code: 'portal_access_deactivated',
  message: 'Ihr Zugang wurde deaktiviert. Wenden Sie sich an Ihren Broker.',
} as const;

/** Kap. 12 events as activity meta.event. */
export const RETENTION_EVENTS = {
  PORTAL_ACCESS_RESTRICTED: 'portal.access_restricted',
  PORTAL_ACCESS_DEACTIVATED: 'portal.access_deactivated',
  RETENTION_SET: 'customer.retention_set',
  RETENTION_DUE: 'customer.retention_due',
  LEGAL_HOLD_SET: 'customer.legal_hold_set',
  LEGAL_HOLD_LIFTED: 'customer.legal_hold_lifted',
  DELETION_APPROVED: 'customer.deletion_approved',
  DELETION_DECLINED: 'customer.deletion_declined',
} as const;

/** The length bound of a hold or decline reason (the DB CHECK). */
export const RETENTION_REASON_MAX = 2000;

/** GET /customer-status/:t/:id -> retention (the header chips and the task section read it). */
export interface CustomerRetentionDto {
  /** YYYY-MM-DD; null until the nightly run made the customer inaktiv. */
  until: string | null;
  legalHold: boolean;
  legalHoldReason: string | null;
  deletionProposalTaskId: string | null;
  deletionApprovedAt: string | null;
  deletionDeclinedAt: string | null;
}

export const EMPTY_RETENTION: CustomerRetentionDto = {
  until: null,
  legalHold: false,
  legalHoldReason: null,
  deletionProposalTaskId: null,
  deletionApprovedAt: null,
  deletionDeclinedAt: null,
};

/** The task's own data on the web (task.meta of a Löschvorschlag). */
export interface RetentionProposalDto {
  confirmation_label: string;
  retention_until: string | null;
}

/** «Aufbewahrung bis 31.12.2036» from the YYYY-MM-DD day. */
export function retentionUntilLabel(until: string): string {
  return `Aufbewahrung bis ${formatIsoDayDe(until)}`;
}

/**
 * The last day a lesend customer can read the portal, `YYYY-MM-DD`: the Zurich calendar day before
 * `access_expires_at`. The nightly run turns the customer inaktiv at 00:05 Zurich on Enddatum + 1 and sets the
 * expiry to the same Zurich wall clock Nachlauf days later, the first day without access; the day before is
 * Enddatum + Nachlauf, the day the Dankesbrief names (`portalNachlaufLastDayIso`: one rule, pinned by the
 * [p6s7-19] spec; this one reads the stored expiry, since a run that comes later only adds days). Every surface that names the run-out (the portal banner, the broker chip, the
 * activity) names this day. Null for an unparsable value.
 */
export function portalLastReadableDayIso(accessExpiresAt: string | Date): string | null {
  const t = new Date(accessExpiresAt);
  if (Number.isNaN(t.getTime())) return null;
  return addDaysIso(zurichDateOf(t), -1);
}

function lastReadableDayDe(accessExpiresAt: string | Date | null): string | null {
  const day = accessExpiresAt === null ? null : portalLastReadableDayIso(accessExpiresAt);
  return day === null ? null : formatIsoDayDe(day);
}

/** «Portalzugang lesend bis 31.03.2027» (activity, broker chip): the last readable day of the expiry. */
export function portalReadOnlyUntilLabel(prefix: string, accessExpiresAt: string | Date | null): string {
  const day = lastReadableDayDe(accessExpiresAt);
  return day === null ? prefix : `${prefix} bis ${day}`;
}

/** The portal banner of a lesend access: the last readable day of the expiry. */
export function portalReadOnlyBanner(accessExpiresAt: string | Date | null): string {
  const day = lastReadableDayDe(accessExpiresAt);
  return day === null
    ? 'Ihr Zugang ist lesend. Laden Sie Ihre Dokumente herunter.'
    : `Ihr Zugang ist bis ${day} lesend. Laden Sie Ihre Dokumente bis dahin herunter.`;
}

/** The Löschvorschlag confirmation label: the Kundennummer, else the name the header shows (review M12). */
export function retentionConfirmationLabel(customerNumber: string | null, displayName: string): string {
  const n = customerNumber?.trim() ?? '';
  return n.length > 0 ? n : displayName;
}
